import React from 'react';
import {
  Slider,
  TextField,
  Select,
  MenuItem,
  Box,
  InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickersActionBar } from '@mui/x-date-pickers/PickersActionBar';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { countries } from 'utils/countriesWithCodes';
import DatahubAPI from 'api/DatahubAPI';
import { CircularProgress } from '@mui/material';

interface ITariffSearchProps {
  inputs: any;
  setInputs: any;
  userHasSearch: boolean;
  effectDate: Date | null;
  setEffectDate: any;
  loadDate: Date | null;
  setLoadDate: any;
  productValue: number;
  setProductValue: any;
  lineSplitShow: boolean;
  loadingCountryShow: boolean;
  ammountShow: boolean;
  tariffResults: any;
  loadingCountry: string;
  setLoadingCountry: any;
  countryFieldKey?: string;
  countryFieldCodes: string[];
  amountInputs: Record<string, number>;
  setAmountInputs: any;
  materialPctByKey: Record<string, number>;
  setMaterialPctByKey: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  readOnly?: boolean;
  isBulkMode?: boolean;
}

const metricUnits = ['mg', 'g', 'kg', 't', 'mL', 'L', 'mm', 'cm', 'm'];

const inputFieldSx = {
  background: 'var(--Background-200, var(--input-color))',
  borderRadius: '8px',
  'min-width': '87px',
  width: 'fit-content',
  maxWidth: 150,
  '& .MuiInputBase-root': {
    width: 'fit-content',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    border: '1px solid var(--secondary-color)',
    height: '45px',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--secondary-color)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--secondary-color)',
    },
    color: 'var(--input-text-color)',
    'padding-right': '10px',
  },
  '& .MuiInputBase-input': {
    color: 'var(--Text-Main, var(--input-text-color))',
    fontFamily: 'var(--font-family)',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '24px',
    textOverflow: 'ellipsis',
  },
  '& .MuiSvgIcon-root': { color: 'var(--input-text-color)' },
};

const sliderSx = {
  px: 1,
  width: '100%',
  marginRight: '5px',
  marginLeft: '5px',
  '& .MuiSlider-rail': { background: 'var(--secondary-color)', opacity: 1 },
  '& .MuiSlider-track': { border: 'none' },
  '& .MuiSlider-thumb': {
    width: 14,
    height: 14,
    '&::before': { boxShadow: 'none' },
  },
};

const COUNTRY_LABELS: Record<string, string> = {
  SMELTCOUNTRY: 'Country of Smelt',
  POURCOUNTRY: 'Country of Pour',
  LOADINGCOUNTRY: 'Loading Country',
};

const titleCase = (s: string) =>
  s
    .toLowerCase()
    .replace(/[_\-]+/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());

const labelFromKey = (key?: string, fallback = 'Loading Country') => {
  if (!key) {
    return fallback;
  }
  const k = key.toUpperCase();
  return COUNTRY_LABELS[k] ?? titleCase(k);
};

const NewTariffRagSearch: React.FC<ITariffSearchProps> = ({
  inputs,
  setInputs,
  effectDate,
  setEffectDate,
  loadDate,
  setLoadDate,
  productValue,
  setProductValue,
  lineSplitShow,
  loadingCountryShow,
  ammountShow,
  tariffResults,
  loadingCountry,
  setLoadingCountry,
  countryFieldKey,
  countryFieldCodes,
  amountInputs,
  setAmountInputs,
  materialPctByKey,
  setMaterialPctByKey,
  readOnly = false,
  isBulkMode,
}) => {
  const [htsOptions, setHtsOptions] = React.useState<any[]>([]);
  const [isHtsLoading, setIsHtsLoading] = React.useState(false);
  const [fetchedPrefix, setFetchedPrefix] = React.useState<string>('');
  const [htsInputValue, setHtsInputValue] = React.useState<string>(
    inputs.htsCode || '',
  );
  const lastLoadingCountryMetaRef = React.useRef<string>('');
  const shouldShowCountryInput = Boolean(
    loadingCountryShow || countryFieldKey || countryFieldCodes.length > 0,
  );

  const formatHtsPretty = (raw: string) => {
    const d = raw.replace(/\D/g, '');
    const parts = [
      d.slice(0, 4),
      d.slice(4, 6),
      d.slice(6, 8),
      d.slice(8, 10),
    ].filter(Boolean);
    return parts.join('.');
  };

  const filterHtsOptions = createFilterOptions<any>({
    matchFrom: 'any',
    stringify: (option: any) => option.code + option.label, // Allows searching by raw digits or dots
  });

  React.useEffect(() => {
    if (!shouldShowCountryInput) {
      lastLoadingCountryMetaRef.current = '';
      setLoadingCountry('');
      return;
    }

    const metaKey = JSON.stringify({
      key: countryFieldKey || '',
      codes: countryFieldCodes || [],
      originCountry: inputs?.originCountry || '',
      originIso2: inputs?.originIso2 || '',
    });

    const defaultLoadingCountry =
      inputs?.originIso2 || inputs?.originCountry || '';

    if (!loadingCountry || lastLoadingCountryMetaRef.current !== metaKey) {
      setLoadingCountry(defaultLoadingCountry);
      lastLoadingCountryMetaRef.current = metaKey;
    }
  }, [
    shouldShowCountryInput,
    countryFieldKey,
    countryFieldCodes,
    inputs?.originCountry,
    inputs?.originIso2,
  ]);

  const isDollarFocused = React.useRef(false);

  const initialMaterialPct = Number.isFinite(inputs.materialPct)
    ? inputs.materialPct
    : 0;

  const [materialDollarInput, setMaterialDollarInput] = React.useState(
    productValue && productValue > 0
      ? ((productValue * initialMaterialPct) / 100).toFixed(2)
      : '',
  );

  React.useEffect(() => {
    // ADD THIS CHECK: Don't overwrite the input if the user is typing in it
    if (isDollarFocused.current) {
      return;
    }

    if (
      productValue &&
      productValue > 0 &&
      Number.isFinite(inputs.materialPct)
    ) {
      const amt = (productValue * inputs.materialPct) / 100;
      setMaterialDollarInput(amt.toFixed(2));
    } else {
      setMaterialDollarInput('');
    }
  }, [productValue, inputs.materialPct]);

  const clamp01pct = (n: number) => {
    const num = Number(n);
    if (!Number.isFinite(num)) {
      return 0;
    }
    const clamped = Math.max(0, Math.min(100, num));
    return Math.round(clamped * 100) / 100;
  };

  const boundedPctUpdate = (
    prev: Record<string, number>,
    key: string,
    rawNext: number,
    orderKeys: string[],
  ) => {
    const next = { ...prev };
    next[key] = clamp01pct(rawNext);

    const keys = (
      orderKeys && orderKeys.length ? orderKeys : Object.keys(next)
    ).filter((k) => k in next);
    const sum = keys.reduce((s, k) => s + (Number(next[k]) || 0), 0);
    let overflow = Math.max(0, sum - 100);

    for (const k of keys) {
      if (overflow <= 0) {
        break;
      }
      if (k === key) {
        continue;
      }
      const cur = Number(next[k]) || 0;
      if (cur <= 0) {
        continue;
      }
      const take = Math.min(cur, overflow);
      next[k] = clamp01pct(cur - take);
      overflow = overflow - take;
    }

    if (overflow > 0) {
      next[key] = clamp01pct((Number(next[key]) || 0) - overflow);
    }

    return next;
  };

  const parseCurrencyToNumber = (raw: string): number => {
    let s = raw.replace(/[^0-9.]/g, '');
    const parts = s.split('.');
    if (parts.length > 2) {
      s = `${parts[0]}.${parts[1]}`;
    }
    if (parts[1] && parts[1].length > 2) {
      s = `${parts[0]}.${parts[1].slice(0, 2)}`;
    }
    if (s === '' || s === '.') {
      return 0;
    }
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  };

  const getEstimatedDutyDisplay = (rateRaw: string, unit: string, qty: any) => {
    if (!rateRaw) return '';

    // 1. Escape the unit string for regex safety (e.g. "m2" -> "m2")
    const safeUnit = unit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // 2. Regex to find the specific rate for this unit
    // Matches: (Number) (Currency) ... / (Unit)
    // Example: "3¢/kg" -> matches "3" and "¢"
    const regex = new RegExp(
      `([\\d\\.]+)\\s*(¢|c|cents?|\\$|usd)\\s*(?:\\/|per)\\s*${safeUnit}`,
      'i',
    );

    const match = rateRaw.match(regex);

    if (match) {
      let val = parseFloat(match[1]);
      const currency = match[2].toLowerCase();

      // Convert cents to dollars
      if (['¢', 'c', 'cent', 'cents'].includes(currency)) {
        val = val / 100;
      }

      // Calculate Total: Rate * Quantity
      const numericQty = Number(qty) || 0;
      const total = val * numericQty;

      return `$${total.toFixed(2)}`;
    }

    // Fallback: If we can't parse it (e.g. complex string), show the original text
    return rateRaw;
  };

  type COpt =
    | string
    | {
        name?: string;
        label?: string;
        countryName?: string;
        iso2?: string;
        alpha2?: string;
        alpha2Code?: string;
        usCustomsCountryCode?: string;
        country?: {
          name?: string;
          countryName?: string;
          iso2?: string;
          alpha2?: string;
          alpha2Code?: string;
        };
      };

  const getName = (o: COpt) => (typeof o === 'string' ? o : o?.name ?? '');
  const getIso = (o: COpt) =>
    typeof o === 'string' ? '' : o?.iso2 ?? ''?.toUpperCase();
  const displayLabel = (o: COpt) => {
    const n = getName(o);
    const code = getIso(o);
    return code ? `${n} - ${code}` : n;
  };

  const UNIT_LABELS: Record<string, string> = {
    'line/gross': 'Gross Lines',
    pcs: 'Pieces',
    prs: 'Pairs',
    m2: 'm²',
    cm2: 'cm²',
    m3: 'm³',
    cm3: 'cm³',
    kw: 'kW',
    kwh: 'kWh',
  };
  const prettyUnit = (u: string) => UNIT_LABELS[u] ?? u;

  const findCountryOption = (valueIsoOrName: string | undefined | null) => {
    if (!valueIsoOrName) {
      return null;
    }
    const v = String(valueIsoOrName).toUpperCase();
    return (
      (countries as COpt[]).find((c) => getIso(c)?.toUpperCase() === v) ||
      (countries as COpt[]).find((c) => getName(c).toUpperCase() === v) ||
      null
    );
  };

  const loadingCountryLabel = labelFromKey(countryFieldKey, 'Country');
  const codeSuffix =
    Array.isArray(countryFieldCodes) && countryFieldCodes.length
      ? ` - For ${countryFieldCodes.join(', ')}`
      : '';
  const loadingCountryFinalLabel = `${loadingCountryLabel}${codeSuffix}`;

  const toDateOnly = (d: Date | null): Date | null => {
    if (!d) {
      return null;
    }
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };
  const isAfter = (a: Date | null, b: Date | null): boolean =>
    Boolean(a && b && a.getTime() > b.getTime());
  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());

  React.useEffect(() => {
    const ld = toDateOnly(loadDate);
    const ed = toDateOnly(effectDate);
    if (isAfter(ld, ed)) {
      setLoadDate(ed);
    }
  }, [effectDate, loadDate]);

  // FIX: Use pointerEvents to disable interaction without changing visual style
  // Do NOT add backgroundColor or opacity changes here to keep it looking "live"
  const disabledSx = readOnly
    ? {
        pointerEvents: 'none' as const,
      }
    : {};

  const disabledSelectorSx = readOnly
    ? {
        pointerEvents: 'none' as const,
      }
    : {};

  return (
    <div className='screening-search-container-full'>
      <div className='screening-inputs-list'>
        <div className='screening-input-row'>
          <div className='screening-label-input'>
            <div style={{ width: '-webkit-fill-available' }}>
              <div className='screening-input-group' style={{ marginTop: 0 }}>
                <div className='screening-input-minors'>
                  {/* HTS Code */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '50%',
                      gap: '3px',
                    }}
                  >
                    <label>HTS Code</label>
                    <Autocomplete
                      freeSolo
                      options={htsOptions}
                      loading={isHtsLoading}
                      readOnly={readOnly}
                      // Format the selected code with dots inside the input box
                      getOptionLabel={(option) =>
                        typeof option === 'string'
                          ? option
                          : formatHtsPretty(option.code)
                      }
                      renderOption={(props, option) => (
                        <li {...props} key={option.code}>
                          {option.label}
                        </li>
                      )}
                      filterOptions={filterHtsOptions}
                      value={
                        inputs.htsCode
                          ? htsOptions.find(
                              (o) =>
                                o.code === inputs.htsCode.replace(/\D/g, ''),
                            ) || {
                              code: inputs.htsCode.replace(/\D/g, ''),
                              label: htsInputValue || inputs.htsCode,
                            }
                          : null
                      }
                      isOptionEqualToValue={(option, val) =>
                        option.code === val.code
                      }
                      inputValue={htsInputValue}
                      onInputChange={async (event, newInputValue, reason) => {
                        // 1. Fully wipe everything on clear
                        if (reason === 'clear') {
                          setHtsInputValue('');
                          setInputs({ ...inputs, htsCode: '' });
                          setHtsOptions([]);
                          setFetchedPrefix('');
                          return;
                        }

                        setHtsInputValue(newInputValue);

                        // 2. THIS FIXES THE RESET BUG:
                        // Keep the actual form state in sync with backspaces/typing
                        if (reason === 'input') {
                          setInputs({
                            ...inputs,
                            htsCode: newInputValue.replace(/\D/g, ''),
                          });
                        }

                        const digits = newInputValue.replace(/\D/g, '');

                        if (digits.length >= 6) {
                          const prefix = digits.slice(0, 6);

                          if (prefix !== fetchedPrefix) {
                            setFetchedPrefix(prefix);
                            setIsHtsLoading(true);
                            try {
                              const formattedPrefix = formatHtsPretty(prefix);
                              const res = await DatahubAPI.retrieveHTSCode({
                                code: formattedPrefix,
                                country: inputs.originCountry || '',
                              });

                              const hits =
                                res?.result?.result?.body?.hits?.hits || [];
                              const optionsMap = new Map();

                              hits.forEach((hit: any) => {
                                const src = hit._source;
                                if (src && src['HS Code']) {
                                  const rawCode = String(src['HS Code'])
                                    .split(' - ')[0]
                                    .trim();
                                  const d = rawCode.replace(/\D/g, '');

                                  if (d.length >= 10) {
                                    const code10 = d.slice(0, 10);
                                    if (!optionsMap.has(code10)) {
                                      const pretty = formatHtsPretty(code10);
                                      const desc = src['Product Name'] || '';
                                      optionsMap.set(code10, {
                                        code: code10,
                                        label: `${pretty} - ${desc}`,
                                      });
                                    }
                                  }
                                }
                              });
                              setHtsOptions(Array.from(optionsMap.values()));
                            } catch (e) {
                              console.error(
                                'Error fetching HTS prefix options',
                                e,
                              );
                            } finally {
                              setIsHtsLoading(false);
                            }
                          }
                        } else if (digits.length < 6) {
                          setHtsOptions([]);
                          setFetchedPrefix('');
                        }
                      }}
                      onChange={(event, newValue) => {
                        if (readOnly) return;
                        if (typeof newValue === 'string') {
                          setInputs({
                            ...inputs,
                            htsCode: newValue.replace(/\D/g, ''),
                          });
                        } else if (newValue && newValue.code) {
                          setInputs({ ...inputs, htsCode: newValue.code });
                        } else {
                          setInputs({ ...inputs, htsCode: '' });
                        }
                      }}
                      componentsProps={{
                        popper: {
                          sx: {
                            '& .MuiAutocomplete-option': {
                              color: 'var(--text-primary-white) !important',
                            },
                          }, // Higher z-index to sit above sidebar/modals
                        },
                        paper: {
                          sx: {
                            color: 'var(--text-primary-white) !important',
                          },
                        },
                      }}
                      sx={{
                        ...disabledSelectorSx,
                        background: 'var(--Background-200, var(--input-color))',
                        borderRadius: '8px',
                        width: '100%',
                        '& .MuiAutocomplete-input': {
                          color: 'var(--Text-Main, var(--input-text-color))',
                          fontFamily: 'var(--font-family)',
                          fontSize: '14px',
                          fontWeight: 400,
                          lineHeight: '24px',
                        },
                        // Removed the strict padding overrides here to restore the Clear Icon
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          border: '1px solid var(--secondary-color)',
                          minHeight: '45px',
                          paddingTop: '0',
                          paddingBottom: '0',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'var(--secondary-color)',
                          },
                          color: 'var(--input-text-color)',
                          '&.Mui-focused': {
                            color: 'var(--focused-text-color)',
                          },
                        },
                        '& .MuiAutocomplete-clearIndicator': {
                          color: 'var(--focused-text-color)',
                          visibility: 'visible',
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder='Enter HTS Code'
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {isHtsLoading ? (
                                  <CircularProgress color='inherit' size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                    />
                  </div>

                  {/* Origin Country */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '50%',
                      gap: '3px',
                    }}
                  >
                    <label>Origin Country</label>
                    <Autocomplete
                      options={countries as COpt[]}
                      getOptionLabel={(option) => displayLabel(option)}
                      isOptionEqualToValue={(opt, val) =>
                        (Boolean(getIso(opt)) && getIso(opt) === getIso(val)) ||
                        getName(opt) === getName(val)
                      }
                      componentsProps={{
                        popper: {
                          sx: {
                            '& .MuiAutocomplete-option': {
                              color: 'var(--text-primary-white) !important',
                            },
                          }, // Higher z-index to sit above sidebar/modals
                        },
                        paper: {
                          sx: {
                            color: 'var(--text-primary-white) !important',
                          },
                        },
                      }}
                      sx={{
                        ...disabledSelectorSx,
                        '::placeholder': { opacity: 1 },
                        background: 'var(--Background-200, var(--input-color))',
                        borderRadius: '8px',
                        height: '45px',
                        '& .MuiAutocomplete-input': {
                          color: 'var(--Text-Main, var(--input-text-color))',
                          fontFamily: 'var(--font-family)',
                          fontSize: '14px',
                          fontWeight: 400,
                          lineHeight: '24px',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          border: 'none',
                          background:
                            'var(--Background-200, var(--input-color))',
                          borderRadius: '8px',
                          height: '45px',
                        },
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          border: '1px solid var(--secondary-color)',
                          height: '45px',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'var(--secondary-color)',
                          },
                          color: 'var(--input-text-color)',
                          '&.Mui-focused': {
                            color: 'var(--focused-text-color)',
                          },
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'var(--focused-text-color)',
                        },
                      }}
                      onChange={(_, newValue) =>
                        setInputs({
                          ...inputs,
                          originCountry: getName(newValue as COpt) || '',
                          originIso2: getIso(newValue as COpt) || '',
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder='Select Country of Origin'
                        />
                      )}
                      value={findCountryOption(
                        inputs.originIso2 || inputs.originCountry,
                      )}
                    />
                  </div>
                </div>

                {/* Product Value */}
                <div className='screening-input-minors'>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '50%',
                      gap: '3px',
                    }}
                  >
                    <label>Product Value</label>
                    <div
                      className='screening-input-field-container'
                      style={{ position: 'relative', ...disabledSx }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          left: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: 'var(--input-text-color)',
                          pointerEvents: 'none',
                        }}
                      >
                        $
                      </span>
                      <input
                        id='value'
                        type='text'
                        placeholder='Enter a Value of Products'
                        className='screening-input-field-content'
                        name='value'
                        value={String(productValue)}
                        onChange={(e) =>
                          setProductValue(parseCurrencyToNumber(e.target.value))
                        }
                        style={{ paddingLeft: 20 }}
                        readOnly={readOnly} // Use readOnly
                      />
                      {!readOnly && (
                        <svg
                          onClick={() => setProductValue(0)}
                          style={{
                            cursor: 'pointer',
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                          }}
                          xmlns='http://www.w3.org/2000/svg'
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                          fill='none'
                        >
                          <path
                            d='M12 4L4 12M4 4L12 12'
                            stroke='var(--focused-text-color)'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      )}
                    </div>
                  </div>

                  {/* Transportation Method */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '25%',
                      gap: '3px',
                    }}
                  >
                    <label>Transportation Method</label>
                    <Autocomplete
                      // Remove disabled prop
                      options={['Ocean', 'Air', 'Land']}
                      getOptionLabel={(option) => option}
                      componentsProps={{
                        popper: {
                          sx: {
                            '& .MuiAutocomplete-option': {
                              color: 'var(--text-primary-white) !important',
                            },
                          }, // Higher z-index to sit above sidebar/modals
                        },
                        paper: {
                          sx: {
                            color: 'var(--text-primary-white) !important',
                          },
                        },
                      }}
                      sx={{
                        ...disabledSx,
                        background: 'var(--Background-200, var(--input-color))',
                        borderRadius: '8px',
                        height: '45px',
                        '& .MuiAutocomplete-input': {
                          color: 'var(--Text-Main, var(--input-text-color))',
                          fontFamily: 'Inter',
                          fontSize: '14px',
                          fontWeight: 400,
                          lineHeight: '24px',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          border: 'none',
                        },
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          border: '1px solid var(--secondary-color)',
                          height: '45px',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'var(--input-placeholder-color)',
                          },
                          color: 'var(--input-text-color)',
                          '&.Mui-focused': {
                            color: 'var(--focused-text-color)',
                          },
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'var(--focused-text-color)',
                        },
                      }}
                      value={inputs.transportMethod || 'Ocean'}
                      onChange={(_, newValue) => {
                        setInputs({
                          ...inputs,
                          transportMethod: newValue || 'Ocean',
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder='Select Transportation Method'
                        />
                      )}
                    />
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '25%',
                      gap: '3px',
                    }}
                  >
                    <label>Entry Date</label>
                    <DatePicker<Date>
                      // Remove disabled prop
                      value={effectDate}
                      onChange={(newValue: Date | null) => {
                        const ed = toDateOnly(newValue);
                        if (!ed) {
                          setEffectDate(null);
                          return;
                        }
                        const ld = toDateOnly(loadDate);
                        if (ld && ld.getTime() > ed.getTime()) {
                          setLoadDate(ed);
                        }
                        setEffectDate(ed);
                      }}
                      slots={{
                        actionBar: PickersActionBar,
                        textField: TextField,
                      }}
                      slotProps={{
                        actionBar: { actions: ['today'] },
                        textField: {
                          placeholder: 'Select Effective Date',
                          size: 'small',
                          fullWidth: true,
                          sx: {
                            ...disabledSx,
                            height: '45px',
                            '& .MuiInputBase-root': {
                              backgroundColor: 'var(--input-color)',
                              borderRadius: '8px',
                              height: '45px',
                              paddingRight: '8px',
                              color: 'var(--input-text-color)',
                            },
                            '& input': {
                              color: 'var(--input-text-color)',
                              fontSize: '14px',
                              fontWeight: 500,
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--secondary-color)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--input-placeholder-color)',
                            },
                            '& .MuiFormLabel-root': {
                              color: 'var(--focused-text-color)',
                              fontSize: '14px',
                            },
                            '& .MuiSvgIcon-root': {
                              color: 'var(--focused-text-color)',
                            },
                          },
                        },
                      }}
                    />
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '25%',
                      gap: '3px',
                    }}
                  >
                    <label>Loading Date</label>
                    <DatePicker<Date>
                      // Remove disabled prop
                      value={loadDate}
                      onChange={(newValue: Date | null) => {
                        const ld = newValue ? startOfDay(newValue) : null;
                        if (!ld) {
                          setLoadDate(null);
                          return;
                        }
                        const ed = effectDate ? startOfDay(effectDate) : null;
                        if (ed && ld.getTime() > ed.getTime()) {
                          setEffectDate(ld);
                        }
                        setLoadDate(ld);
                      }}
                      maxDate={effectDate ? startOfDay(effectDate) : undefined}
                      slots={{
                        actionBar: PickersActionBar,
                        textField: TextField,
                      }}
                      slotProps={{
                        actionBar: { actions: ['today'] },
                        textField: {
                          placeholder: 'Loading Date',
                          size: 'small',
                          fullWidth: true,
                          sx: {
                            ...disabledSx,
                            height: '45px',
                            '& .MuiInputBase-root': {
                              backgroundColor: 'var(--input-color)',
                              borderRadius: '8px',
                              height: '45px',
                              paddingRight: '8px',
                              color: 'var(--input-text-color)',
                            },
                            '& input': {
                              color: 'var(--input-text-color)',
                              fontSize: '14px',
                              fontWeight: 500,
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--secondary-color)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--input-placeholder-color)',
                            },
                            '& .MuiFormLabel-root': {
                              color: 'var(--focused-text-color)',
                              fontSize: '14px',
                            },
                            '& .MuiSvgIcon-root': {
                              color: 'var(--focused-text-color)',
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </div>

                {(lineSplitShow || shouldShowCountryInput || ammountShow) && (
                  <div
                    className='screening-ai-label-text'
                    style={{ margin: '10px 0px' }}
                  >
                    Additional Information:
                  </div>
                )}

                <div className='screening-input-minors'>
                  {/* 🔽 Dynamic Loading Country */}
                  {shouldShowCountryInput && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '40%',
                        gap: '3px',
                      }}
                    >
                      <label>{loadingCountryFinalLabel}</label>
                      <Autocomplete
                        // Remove disabled prop
                        options={countries as COpt[]}
                        getOptionLabel={(option) => displayLabel(option)}
                        isOptionEqualToValue={(opt, val) =>
                          (Boolean(getIso(opt)) &&
                            getIso(opt) === getIso(val)) ||
                          getName(opt) === getName(val)
                        }
                        componentsProps={{
                          popper: {
                            sx: {
                              '& .MuiAutocomplete-option': {
                                color: 'var(--text-primary-white) !important',
                              },
                            }, // Higher z-index to sit above sidebar/modals
                          },
                          paper: {
                            sx: {
                              color: 'var(--text-primary-white) !important',
                            },
                          },
                        }}
                        sx={{
                          ...disabledSx,
                          background:
                            'var(--Background-200, var(--input-color))',
                          borderRadius: '8px',
                          height: '45px',
                          '& .MuiAutocomplete-input': {
                            color: 'var(--Text-Main, var(--input-text-color))',
                            fontFamily: 'var(--font-family)',
                            fontSize: '14px',
                            fontWeight: 400,
                            lineHeight: '24px',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            border: 'none',
                            background:
                              'var(--Background-200, var(--inout-color))',
                            borderRadius: '8px',
                            height: '45px',
                          },
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            border: '1px solid var(--secondary-color)',
                            height: '45px',
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--secondary-color)',
                            },
                            color: 'var(--input-text-color)',
                            '&.Mui-focused': {
                              color: 'var(--inout-text-color)',
                            },
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'var(--input-text-color)',
                          },
                        }}
                        onChange={(_, newValue) =>
                          setLoadingCountry(
                            getIso(newValue as COpt) ||
                              getName(newValue as COpt) ||
                              '',
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={`Select ${loadingCountryLabel}`}
                          />
                        )}
                        value={findCountryOption(loadingCountry)}
                      />
                    </div>
                  )}

                  {/* Material split(s) */}
                  {lineSplitShow && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '40%',
                        gap: '3px',
                      }}
                    >
                      {(tariffResults?.lineSplitFields?.length ?? 0) <= 1
                        ? (() => {
                            const firstKey =
                              (tariffResults?.lineSplitFields &&
                                tariffResults.lineSplitFields[0]?.key) ||
                              'SPLIT';
                            const labelText =
                              tariffResults?.lineSplitFields?.[0]?.label ??
                              tariffResults?.lineSplitLabel ??
                              'Material Percent';
                            const value = (materialPctByKey?.[firstKey] ??
                              (Number.isFinite(inputs.materialPct)
                                ? inputs.materialPct
                                : 0)) as number;
                            const orderKeys = (
                              tariffResults?.lineSplitFields ?? []
                            ).map((x: any) => x.key);

                            const syncDollarFromPct = (pct: number) => {
                              if (!productValue || productValue <= 0) {
                                setMaterialDollarInput('');
                                return;
                              }
                              const amt = (productValue * pct) / 100;
                              setMaterialDollarInput(amt.toFixed(2));
                            };

                            return (
                              <>
                                <label>
                                  {labelText.replace(
                                    /([a-z])([A-Z])/g,
                                    '$1 $2',
                                  )}
                                </label>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    gap: 1,
                                    alignItems: 'center',
                                  }}
                                >
                                  {/* Slider */}
                                  <Slider
                                    // Remove disabled prop
                                    sx={{ ...sliderSx, ...disabledSx }} // Apply disabledSx here too
                                    value={value}
                                    onChange={(_: any, v: any) => {
                                      if (readOnly) {
                                        return;
                                      } // Guard manually
                                      const n = clamp01pct(
                                        Array.isArray(v)
                                          ? Number(v[0])
                                          : Number(v),
                                      );
                                      setInputs({ ...inputs, materialPct: n });
                                      setMaterialPctByKey((prev) =>
                                        boundedPctUpdate(
                                          prev ?? {},
                                          firstKey,
                                          n,
                                          orderKeys,
                                        ),
                                      );
                                      syncDollarFromPct(n);
                                    }}
                                    valueLabelDisplay='off'
                                    step={0.01}
                                    min={0}
                                    max={100}
                                  />

                                  {/* % input */}
                                  <TextField
                                    // Remove disabled prop
                                    sx={{
                                      ...inputFieldSx,
                                      ...disabledSx,
                                      minWidth: 80,
                                      width: 'fit-content',
                                      maxWidth: 150,
                                      '& .MuiInputBase-root': { width: '100%' },
                                      '& .MuiInputBase-input': {
                                        textAlign: 'right',
                                        overflow: 'visible',
                                        textOverflow: 'clip',
                                      },
                                    }}
                                    value={value}
                                    onChange={(e: any) => {
                                      if (readOnly) {
                                        return;
                                      } // Guard manually
                                      const n = clamp01pct(e.target.value);
                                      setInputs({ ...inputs, materialPct: n });
                                      setMaterialPctByKey((prev) =>
                                        boundedPctUpdate(
                                          prev ?? {},
                                          firstKey,
                                          n,
                                          orderKeys,
                                        ),
                                      );
                                      syncDollarFromPct(n);
                                    }}
                                    type='number'
                                    inputProps={{
                                      min: 0,
                                      max: 100,
                                      step: 0.01,
                                    }}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment
                                          position='end'
                                          disableTypography
                                          sx={{
                                            color: 'var(--input-text-color)',
                                            fontFamily: 'var(--font-family)',
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            margin: '0px',
                                          }}
                                        >
                                          %
                                        </InputAdornment>
                                      ),
                                    }}
                                  />

                                  {/* $ input */}
                                  <TextField
                                    // Remove disabled prop
                                    sx={{
                                      ...inputFieldSx,
                                      ...disabledSx,
                                      minWidth: 100,
                                      width: 'fit-content',
                                      maxWidth: 150,
                                      '& .MuiInputBase-root': { width: '100%' },
                                      '& .MuiInputBase-input': {
                                        textAlign: 'right',
                                        overflow: 'visible',
                                        textOverflow: 'clip',
                                      },
                                    }}
                                    value={materialDollarInput}
                                    onChange={(e: any) => {
                                      if (readOnly) {
                                        return;
                                      } // Guard manually
                                      const raw = e.target.value;
                                      if (raw === '') {
                                        setMaterialDollarInput('');
                                        const n = 0;
                                        setInputs({
                                          ...inputs,
                                          materialPct: n,
                                        });
                                        setMaterialPctByKey((prev) =>
                                          boundedPctUpdate(
                                            prev ?? {},
                                            firstKey,
                                            n,
                                            orderKeys,
                                          ),
                                        );
                                        return;
                                      }
                                      if (!/^\d*\.?\d{0,2}$/.test(raw)) {
                                        return;
                                      }
                                      setMaterialDollarInput(raw);
                                      const safeDollar =
                                        parseCurrencyToNumber(raw);
                                      if (!productValue || productValue <= 0) {
                                        const n = 0;
                                        setInputs({
                                          ...inputs,
                                          materialPct: n,
                                        });
                                        setMaterialPctByKey((prev) =>
                                          boundedPctUpdate(
                                            prev ?? {},
                                            firstKey,
                                            n,
                                            orderKeys,
                                          ),
                                        );
                                        return;
                                      }
                                      const pctFromDollar =
                                        (safeDollar / productValue) * 100;
                                      const n = clamp01pct(pctFromDollar);
                                      setInputs({ ...inputs, materialPct: n });
                                      setMaterialPctByKey((prev) =>
                                        boundedPctUpdate(
                                          prev ?? {},
                                          firstKey,
                                          n,
                                          orderKeys,
                                        ),
                                      );
                                    }}
                                    // ADD THIS: Set ref to true on focus
                                    onFocus={() => {
                                      isDollarFocused.current = true;
                                    }}
                                    // UPDATE THIS: Set ref to false on blur
                                    onBlur={() => {
                                      isDollarFocused.current = false; // Mark as not focused

                                      // Existing blur logic...
                                      if (materialDollarInput === '') {
                                        return;
                                      }
                                      const safeDollar =
                                        parseCurrencyToNumber(
                                          materialDollarInput,
                                        );
                                      setMaterialDollarInput(
                                        Number.isFinite(safeDollar)
                                          ? safeDollar.toFixed(2)
                                          : '0.00',
                                      );
                                    }}
                                    type='text'
                                    inputProps={{ inputMode: 'decimal' }}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment
                                          position='start'
                                          disableTypography
                                          sx={{
                                            color: 'var(--input-text-color)',
                                            fontFamily: 'var(--font-family)',
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            margin: '0px',
                                          }}
                                        >
                                          $
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Box>
                              </>
                            );
                          })()
                        : (tariffResults?.lineSplitFields ?? []).map(
                            (f: any, idx: number) => {
                              const orderKeys = (
                                tariffResults?.lineSplitFields ?? []
                              ).map((x: any) => x.key);
                              const value = (materialPctByKey?.[f.key] ??
                                0) as number;
                              const labelText =
                                f.label || 'Material Percentage';

                              return (
                                <React.Fragment key={f.key}>
                                  <label>{labelText}</label>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      gap: 1,
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Slider
                                      // Remove disabled prop
                                      sx={{ ...sliderSx, ...disabledSx }}
                                      value={value}
                                      onChange={(_: any, v: any) => {
                                        if (readOnly) {
                                          return;
                                        }
                                        const n = clamp01pct(
                                          Array.isArray(v)
                                            ? Number(v[0])
                                            : Number(v),
                                        );
                                        setMaterialPctByKey((prev) =>
                                          boundedPctUpdate(
                                            prev ?? {},
                                            f.key,
                                            n,
                                            orderKeys,
                                          ),
                                        );
                                        if (idx === 0) {
                                          setInputs({
                                            ...inputs,
                                            materialPct: n,
                                          });
                                        }
                                      }}
                                      valueLabelDisplay='off'
                                      step={0.01}
                                      min={0}
                                      max={100}
                                    />
                                    <TextField
                                      // Remove disabled prop
                                      sx={{ ...inputFieldSx, ...disabledSx }}
                                      value={value}
                                      onChange={(e: any) => {
                                        if (readOnly) {
                                          return;
                                        }
                                        const n = clamp01pct(e.target.value);
                                        setMaterialPctByKey((prev) =>
                                          boundedPctUpdate(
                                            prev ?? {},
                                            f.key,
                                            n,
                                            orderKeys,
                                          ),
                                        );
                                        if (idx === 0) {
                                          setInputs({
                                            ...inputs,
                                            materialPct: n,
                                          });
                                        }
                                      }}
                                      type='number'
                                      inputProps={{
                                        min: 0,
                                        max: 100,
                                        step: 0.01,
                                      }}
                                      InputProps={{
                                        endAdornment: (
                                          <InputAdornment
                                            position='end'
                                            disableTypography
                                          >
                                            %
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </Box>
                                </React.Fragment>
                              );
                            },
                          )}
                    </div>
                  )}

                  {/* Amount-based base duty */}
                  {ammountShow && (
                    <>
                      {Array.isArray(tariffResults?.requiredAmountUnits) &&
                      tariffResults.requiredAmountUnits.length > 0 ? (
                        tariffResults.requiredAmountUnits.map(
                          (unit: string) => (
                            <div
                              key={unit}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '20%',
                                gap: '3px',
                              }}
                            >
                              <label>
                                Ad Valorem -{' '}
                                {getEstimatedDutyDisplay(
                                  tariffResults.baseDutyRateRaw,
                                  unit,
                                  amountInputs[unit],
                                )}
                              </label>
                              <Box
                                sx={{ display: 'flex', gap: 1, height: '45px' }}
                              >
                                <TextField
                                  sx={{
                                    ...inputFieldSx,
                                    width: '100%',
                                    ...disabledSx,
                                  }}
                                  placeholder='0'
                                  type='number'
                                  value={
                                    Number.isFinite(Number(amountInputs[unit]))
                                      ? amountInputs[unit]
                                      : ''
                                  }
                                  onChange={(e: any) => {
                                    if (readOnly) {
                                      return;
                                    }
                                    const v =
                                      e.target.value === ''
                                        ? NaN
                                        : Number(e.target.value);
                                    setAmountInputs({
                                      ...amountInputs,
                                      [unit]: Number.isFinite(v) ? v : 0,
                                    });
                                  }}
                                  inputProps={{ min: 0 }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment
                                        position='end'
                                        disableTypography
                                        sx={{
                                          color: 'var(--input-text-color)',
                                          fontFamily: 'var(--font-family)',
                                          fontSize: '14px',
                                          fontWeight: 400,
                                        }}
                                      >
                                        {prettyUnit(unit)}
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Box>
                            </div>
                          ),
                        )
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '20%',
                            gap: '3px',
                          }}
                        >
                          <label>Product Amount</label>
                          <Box sx={{ display: 'flex', gap: 1, height: '45px' }}>
                            <TextField
                              // Remove disabled prop
                              sx={{
                                ...inputFieldSx,
                                width: '60%',
                                ...disabledSx,
                              }}
                              placeholder='0'
                              type='number'
                              value={inputs.amountValue ?? ''}
                              onChange={(e: any) => {
                                if (readOnly) {
                                  return;
                                }
                                setInputs({
                                  ...inputs,
                                  amountValue:
                                    e.target.value === ''
                                      ? ''
                                      : Number(e.target.value),
                                });
                              }}
                              inputProps={{ min: 0 }}
                            />
                            <Select
                              // Remove disabled prop
                              sx={{
                                ...inputFieldSx,
                                width: '40%',
                                ...disabledSx,
                              }}
                              value={inputs.amountUnit || 'kg'}
                              onChange={(e: any) => {
                                if (readOnly) {
                                  return;
                                }
                                setInputs({
                                  ...inputs,
                                  amountUnit: e.target.value,
                                });
                              }}
                              displayEmpty
                            >
                              {metricUnits.map((u) => (
                                <MenuItem key={u} value={u}>
                                  {u}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Conditionally hide this entire button/disclaimer section if in ReadOnly Mode */}
      {!isBulkMode && (
        <div className='tariff-buttons-cont'>
          <div className='disclaimer-text'>
            Disclaimer: Users are responsible for manually verifying product
            scope, effective dates, list numbers, applicable exemptions, and
            FTAs - the outputs of this tool are an unofficial opinion of the
            BITE Data Corporation. It is intended to assist in identifying
            potential tariff implications and should be used alongside a
            comprehensive compliance review. Reliance upon this advice does not
            constitute reasonable care under the Customs Modernization Act. In
            order to exercise the highest degree of reasonable care, BITE Data
            Corporation recommends that users perform a complete review, which
            may include application for a binding ruling from U.S. Customs and
            Border Protection. Should you need further assistance in this
            matter, please contact us at{' '}
            <a href='mailto:info@bitedata.io'>info@bitedata.io</a>, where we can
            direct you to additional resources for your trade compliance needs.
            {/* <br />
            <br />
            View recent and upcoming data updates{' '}
            <a href='https://bitedata.notion.site/' target='_blank' rel='noopener noreferrer'>
              here
            </a> */}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          ></div>
        </div>
      )}
    </div>
  );
};

export default NewTariffRagSearch;
